import { RecipeFormData } from '@/types/recipe';
import { fenceUntrusted } from '@/services/assistant/security';
import { isValidPersonaId, loadPersonaChatProfile } from '@/services/studioChat/personaLoader';

export const PROMPT_TEMPLATES = {
  IMAGE_GENERATION: (recipeDetails: string) => `
Analyze the following recipe details and generate a detailed, vivid image prompt suitable for an AI image generator.
The prompt should inspire a photorealistic image of the finished dish, considering plating, lighting, style (e.g., rustic, modern, bright, moody), and overall appeal.
Focus on creating a prompt that would result in a high-quality food photograph.

Recipe Details:
${recipeDetails}

Generated Image Prompt:`,

  RECIPE_CLEANUP: (recipeMarkdown: string) => `
Review the following recipe, which is in Markdown format.
Identify any formatting issues, unclear instructions, missing sections (e.g., prep time, cook time, servings), or areas for improvement in clarity and structure.
Provide a list of suggestions to enhance the recipe's readability and usability. If possible, also provide the cleaned-up Markdown.

Recipe Markdown:
---
${recipeMarkdown}
---

Suggestions and Cleaned-up Recipe (if applicable):`,

  SUBSTITUTIONS: (ingredient: string, dietaryRestrictions: string | null, recipeTitle: string) => `
For the recipe "${recipeTitle}", suggest suitable alternatives for the ingredient: "${ingredient}".
${dietaryRestrictions ? `Consider the following dietary restrictions: ${dietaryRestrictions}.` : ''}
Explain why each alternative is suitable and any potential impact on the recipe.

Ingredient: ${ingredient}
${dietaryRestrictions ? `Dietary Restrictions: ${dietaryRestrictions}` : ''}
Recipe: ${recipeTitle}

Suggested Alternatives:`,

  UNIT_CONVERSION: (measurements: string, targetSystem: 'metric' | 'imperial') => `
Convert the following cooking measurements to the ${targetSystem} system.
Provide the conversions clearly.

Original Measurements:
${measurements}

Target System: ${targetSystem}

Converted Measurements:`,

  DESCRIPTION_GENERATION: (recipeMarkdown: string) => `
Based on the following full recipe markdown, please write a single, concise, and appealing description.
The description should summarize the dish's key characteristics and ingredients.

Recipe Content:
---
${recipeMarkdown}
---

Generated Description:`,

  INTENT_DETECTION: (message: string, recipeTitle: string) => `
Analyze this user message in the context of a recipe conversation.
Recipe: "${recipeTitle}"
User message: "${message}"

Classify the intent as:
- MODIFY: User wants to change the recipe (e.g., "make it spicier", "add garlic", "make vegan")
- QUESTION: User has questions about the recipe (e.g., "how long to cook?", "can I substitute?", "what's a good side dish?")
- CHAT: General conversation or cooking advice (e.g., "tell me about this dish", "cooking tips", "storage advice")
- UNCLEAR: Intent is ambiguous or could be interpreted multiple ways

Return only: MODIFY, QUESTION, CHAT, or UNCLEAR
`,

  RECIPE_QA: (recipe: RecipeFormData, question: string) => `
You are a helpful cooking assistant. The user is viewing this recipe and has a question - DO NOT modify the recipe, just answer their question:
${recipe.title}

Recipe details:
Ingredients: ${recipe.ingredients}
Method: ${recipe.method}
${recipe.notes ? `Notes: ${recipe.notes}` : ''}

User question: ${question}

IMPORTANT: Only provide information and suggestions. Do not change or rewrite the recipe. If asked about substitutions or alternatives, provide helpful suggestions without modifying the actual recipe content.

Provide a helpful, accurate answer about this recipe or cooking technique. Keep responses concise and actionable.
`
  ,
  RECIPE_PARSING: (rawText: string) => `
You are a recipe expert. Convert messy recipe text into clean Markdown, preserving the exact original content — do not skip, summarize, or rephrase anything.

The text is pasted from websites and may have ingredients, instructions, and notes mixed together in any order. Separate everything into the right sections while preserving the original order and groupings.

# Required Output Structure

# [Recipe Title]

## Description
1-2 sentence description of the dish.

## Ingredients
- ingredient 1
- ingredient 2

If the source has sub-groupings, preserve them as bold headers in their original order:

**For the Crust:**
- 2 cups flour
- 1 cup butter

## Method
1. Step one
2. Step two

## Notes
Supplementary info: tips, storage, variations.

# Concrete Example

Input:
"Mom's Apple Pie
A classic American dessert.

Crust:
- 2 cups flour
- 1 cup butter

Filling:
- 6 apples, sliced
- 1 cup sugar

Method:
1. Mix crust ingredients.
2. Fill and bake at 350F for 45 min.

Notes: Best served warm."

Output:

# Mom's Apple Pie

## Description
A classic American apple pie with a buttery crust and sweet apple filling.

## Ingredients

**For the Crust:**
- 2 cups flour
- 1 cup butter

**For the Filling:**
- 6 apples, sliced
- 1 cup sugar

## Method
1. Mix crust ingredients.
2. Fill and bake at 350F for 45 min.

## Notes
Best served warm.

# Category (OPTIONAL)

Only include a final "## Category" section if the recipe clearly fits ONE of these:

- Asian: East/Southeast/South Asian (Chinese, Japanese, Korean, Thai, Vietnamese, Indian, etc.)
- Sri Lankan: Recipes from Sri Lanka or distinctly Sri Lankan dishes
- Mexican: Mexican cuisine, Tex-Mex
- Latin American: Central/South American (Brazilian, Argentine, Peruvian, Colombian, etc.) — NOT Mexican
- Desserts: Cakes, cookies, pastries, sweet treats, puddings
- Drinks: Beverages, cocktails, smoothies, juices
- Sauces: Standalone sauces, condiments, dressings
- To Try: If the recipe is clearly marked as something to try or bookmarked

If the recipe doesn't clearly fit any of these, OMIT the "## Category" section entirely. The user can pick a category manually in the form. Never force a category.

# Rules

1. **COMPLETENESS** — Output the ENTIRE recipe. Never truncate mid-sentence, mid-list, or mid-section, regardless of length. If the recipe is long, the output must still be complete.
2. **ORDERING** — Preserve the original order of ingredients, steps, and sub-groupings. Do not reorder.
3. **FORMATTING** — Ingredients use bullet lists ("-"). Method steps use numbered lists ("1."). Sub-groupings use bold headers ("**For the Crust:**").
4. **DESCRIPTION** — Always write 1-2 sentences. If the source has no description, infer one from the ingredients and method.
5. **TITLE** — Short, appetizing, focused on the dish. Do NOT include dietary tags like "Low-Carb" or "Gluten-Free" in the title.
6. **CATEGORY** — OPTIONAL. Only include if the recipe clearly fits one of the listed categories. If unsure, OMIT. Never force a category.
7. **NO PLACEHOLDERS** — Do not output literal "[placeholder]" text, template variables, or meta-commentary. Output only the recipe.

# Source Text to Convert

${fenceUntrusted(rawText, 'pasted recipe text')}

Convert to organized Markdown:
 `,
};

/**
 * SYSTEM PROMPT: Recipe Creation vs Import Clarification
 *
 * When a user asks to “create”, “add”, “import”, or “format” a recipe, always check for ambiguity:
 * - If the user provides a block of text that looks like a recipe (e.g., includes “ingredients”, “method”, “directions”, or similar), assume they want to import/parse it and add it to their collection.
 * - If the user only provides an idea, a few ingredients, or says “create a recipe for me”, assume they want to use the Studio to invent a new recipe.
 * - If the user’s intent is unclear (for example, they say “can you help me create/add this recipe?” without pasting content, or the content could be interpreted either way), always ask for clarification:
 *   - “Would you like to create a brand new recipe using the Studio, or are you trying to import and format an existing recipe from the web?”
 * - Never proceed with coin-charging or action until the user’s intent is clear.
 */

export const SYSTEM_RECIPE_INTENT_CLARIFICATION = `
When a user requests to create, add, import, or format a recipe, always check for ambiguity:
- If the user provides a block of text that looks like a recipe (e.g., includes “ingredients”, “method”, “directions”, or similar), assume they want to import/parse it and add it to their collection.
- If the user only provides an idea, a few ingredients, or says “create a recipe for me”, assume they want to use the Studio to invent a new recipe.
- If the user’s intent is unclear (for example, they say “can you help me create/add this recipe?” without pasting content, or the content could be interpreted either way), always ask for clarification:
  “Would you like to create a brand new recipe using the Studio, or are you trying to import and format an existing recipe from the web?”
- Never proceed with coin-charging or action until the user’s intent is clear.
`;

export const INTENT_DETECTION_PROMPT = (message: string, recipeTitle: string) => `
Analyze this user message in the context of a recipe conversation.
Recipe: "${recipeTitle}"
User message: "${message}"

Classify the intent as:
- MODIFY: User wants to change the recipe (e.g., "make it spicier", "add garlic", "make vegan")
- QUESTION: User has questions about the recipe (e.g., "how long to cook?", "can I substitute?", "what's a good side dish?")
- CHAT: General conversation or cooking advice (e.g., "tell me about this dish", "cooking tips", "storage advice")
- UNCLEAR: Intent is ambiguous or could be interpreted multiple ways

Return only: MODIFY, QUESTION, CHAT, or UNCLEAR
`;

export const RECIPE_QA_PROMPT = (recipe: RecipeFormData, question: string) => `
You are a helpful cooking assistant. The user is viewing this recipe:
${recipe.title}

Recipe details:
Ingredients: ${recipe.ingredients}
Method: ${recipe.method}
${recipe.notes ? `Notes: ${recipe.notes}` : ''}

User question: ${question}

Provide a helpful, accurate answer about this recipe or cooking technique. Keep responses concise and actionable. If the question isn't directly about this recipe, still provide relevant cooking guidance when possible.
`;

export function buildRecipeModificationPrompt(formData: RecipeFormData, modificationRequest: string, personaId?: string): string {
  // Convert current form data to a simplified markdown-like structure for the AI
  let currentRecipeMarkdown = `# ${formData.title || 'Untitled Recipe'}\n\n`;
  if (formData.description) currentRecipeMarkdown += `${formData.description}\n\n`;

  currentRecipeMarkdown += `## Ingredients\n${formData.ingredients || 'No ingredients listed.'}\n\n`;
  currentRecipeMarkdown += `## Method\n${formData.method || 'No instructions provided.'}\n\n`;

  if (formData.notes) currentRecipeMarkdown += `## Notes\n${formData.notes}\n`;

  // P1.7: persona-aware modifications — inject the chef's chat profile (voice,
  // tone, philosophy) so a modification keeps the persona that wrote the recipe.
  let personaSection = '';
  if (personaId && isValidPersonaId(personaId)) {
    const chatProfile = loadPersonaChatProfile(personaId);
    personaSection = `
**Chef Persona (keep this voice when rewriting):**
---
${chatProfile}
---
Apply the modification while preserving the chef persona's voice, tone and culinary philosophy above. If the modification conflicts with the persona's philosophy (e.g., a minimalist chef asked for elaborate steps), honour the user's request but let the persona's character show in how the recipe is written.

`;
  }

  const prompt = `
You are an expert recipe editor. The user wants to modify the following recipe.
${personaSection}Please apply the requested modification and return the *complete, updated recipe* in the standard Markdown format (Title, Description, ## Ingredients, ## Method, ## Notes, etc.).
Ensure all original sections are present in your response, updated as necessary.

**Current Recipe:**
---
${fenceUntrusted(currentRecipeMarkdown, 'current recipe')}
---

**User's Modification Request:**
${fenceUntrusted(modificationRequest, 'modification request')}

**Your Task:**
Rewrite the entire recipe incorporating the user's request. Maintain the original structure and formatting style.
If the request is unclear or cannot be reasonably applied, explain why in a "Chef's Note" section at the end of the recipe.
Do not just list the changes; provide the full, modified recipe.
You can also add a brief summary of changes under a "## Changes Summary" heading at the very end of your response, after the full recipe.
`;
  return prompt;
}